import { useEffect, useMemo } from "react";
import type { PresentationState } from "@/lib/focus/focus-types";
import { usePresentationStore } from "@/stores/presentation-store";
import { useDesktopMode, type DesktopMode } from "./use-desktop-mode";
import { getWindowDisplayId } from "./use-window-role";

const EMPTY_IDS: string[] = [];
export function resolveCompactFrameOverlayIds({
  mode,
  requestedFrameIds,
  presentation,
}: {
  mode: DesktopMode;
  requestedFrameIds: string[];
  presentation: PresentationState | null;
}): string[] {
  if (
    mode !== "compact" ||
    requestedFrameIds.length === 0 ||
    presentation?.variant !== "focus"
  ) {
    return EMPTY_IDS;
  }

  const presentedIds = new Set(presentation.frameIds);
  return [...new Set(requestedFrameIds)].filter((id) => presentedIds.has(id));
}

export function useActiveCompactFrameOverlayIds(): string[] {
  const mode = useDesktopMode((state) => state.mode);
  const requestedFrameIds = useDesktopMode(
    (state) => state.compactFrameOverlayIds ?? EMPTY_IDS,
  );
  const presentation = usePresentationStore(
    (state) => state.presentation ?? null,
  );

  useEffect(() => {
    if (presentation?.variant !== "focus") {
      if (requestedFrameIds.length > 0) {
        useDesktopMode.getState().clearCompactFrameOverlay();
      }
      return;
    }
    // A Focus on this display floats over the desktop in compact. Another
    // window's passing "nothing in Focus" emptied the list; the Focus is back,
    // so its frames are drawn again instead of staying in Focus unseen. The
    // emptied list forgot whether Back returns to the Workspace: it now
    // returns to the desktop.
    if (
      mode === "compact" &&
      presentation.renderer === "overlay" &&
      requestedFrameIds.length === 0 &&
      presentation.displayId != null &&
      presentation.displayId === getWindowDisplayId()
    ) {
      useDesktopMode.getState().closeWorkspace(presentation.frameIds);
    }
  }, [mode, presentation, requestedFrameIds]);

  return useMemo(
    () =>
      resolveCompactFrameOverlayIds({
        mode,
        requestedFrameIds,
        presentation,
      }),
    [mode, presentation, requestedFrameIds],
  );
}

/**
 * The frames the old canvas draws over the desktop in compact, or null when
 * it shows no compact overlay. The canvas mounts only on the canvas path,
 * where focus is always drawn by the canvas; on the wall path `FocusOverlay`
 * draws compact focus (Part 06 decision 6).
 */
export function useCanvasCompactFrameOverlay(): ReadonlySet<string> | null {
  const ids = useActiveCompactFrameOverlayIds();
  return useMemo(() => (ids.length === 0 ? null : new Set(ids)), [ids]);
}
